/**
 * useEForm — read-only subset
 * Ported from pitstop-ui/src/hooks/use-eform/index.js
 *
 * Stripped: immer, lodash, ajv, getNextPayload, getSchemaWithoutAttachment,
 *   sortSchemaFields, checkIfNested, isDirty, setPayload, Redux/feature hooks.
 * Kept:  getFieldValue, getFullJson, extractCombinedSchema
 * Changed: lodash cloneDeep → structuredClone; ajv schema matching →
 *   required-field heuristic (no new deps).
 */

export function getFieldValue(payload, parentProps) {
  let temp = payload
  for (const key of parentProps) {
    if (temp === undefined || temp === null) return undefined
    temp = temp[key]
  }
  return temp
}

function updatedGetFullJson(schema, payload) {
  let data = payload !== null && payload !== undefined ? structuredClone(payload) : null

  const type =
    Array.isArray(schema?.type) &&
    !schema?.type.includes('array') &&
    (!schema?.type.includes('object') || schema?.['x-uitype'] === 'file-upload')
      ? schema?.type.find(t => t !== 'null')
      : schema?.type

  switch (type) {
    case 'string':
      if (data !== undefined && data !== null) return data
      if (schema.format?.includes('date')) return null
      return ''
    case 'number':
    case 'integer':
      return data ?? null
    case 'boolean':
    case 'radio':
      return data ?? false
    case 'array':
      return data
        ? data.map(item => getFullJson(schema.items, item))
        : [getFullJson(schema.items)].filter(item => item !== undefined && item !== null)
    case 'object':
      if (!data && schema?.['x-uitype'] === 'file-upload') return null
      if (!data) data = {}
      for (const prop in schema.properties) {
        data[prop] = getFullJson(schema.properties[prop], data[prop])
      }
      if (Object.keys(data).length === 0) {
        data = {}
        for (const prop in schema.properties) {
          data[prop] = getFullJson(schema.properties[prop])
        }
      }
      return data
    default:
      if (Array.isArray(schema?.type)) {
        if (schema.type.includes('array')) {
          if (data && Array.isArray(data)) {
            const processed = data
              .map(item => getFullJson(schema.items, item))
              .filter(item => item !== null && item !== undefined)
            return processed.length > 0 ? processed : null
          }
          return null
        }
        if (schema.type.includes('object') && schema['x-uitype'] !== 'file-upload') {
          if (data && typeof data === 'object' && !Array.isArray(data)) {
            let hasValid = false
            for (const prop in schema.properties) {
              const result = getFullJson(schema.properties[prop], data[prop])
              if (result !== null && result !== undefined) {
                data[prop] = result
                hasValid = true
              } else {
                delete data[prop]
              }
            }
            return hasValid ? data : null
          }
          return null
        }
      }
      return data
  }
}

export function getFullJson(schema, payload) {
  return updatedGetFullJson(schema, payload)
}

/**
 * Resolves anyOf/oneOf by picking the first branch whose required fields
 * are all present in the payload. Falls back to the first branch.
 * Replaces the AJV compile/validate approach from pitstop-ui.
 */
export function extractCombinedSchema(schema, payload) {
  let keyword
  if (Array.isArray(schema?.oneOf)) keyword = 'oneOf'
  else if (Array.isArray(schema?.anyOf)) keyword = 'anyOf'

  if (!keyword) return schema
  if (!payload) return schema[keyword][0]

  const match = schema[keyword].find(sub => {
    if (!sub.required || !sub.required.length) return true
    return sub.required.every(
      k => payload[k] !== undefined && payload[k] !== null && payload[k] !== ''
    )
  })
  return match ?? schema[keyword][0]
}

const useEForm = () => ({ getFieldValue, getFullJson, extractCombinedSchema })
export default useEForm
